// ==========================================================================
// PROJECT — Cover color class + mobile logo pin
// ==========================================================================

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);



// ==========================================================================
// RELATED CAROUSEL (mobile) — one caption at a time
// The footer strip is a scroll-snap carousel; only the card in the middle
// keeps its caption. The track has no side padding, so the first and last
// card can never physically reach the centre — hence the two end clamps,
// without which their captions would be unreachable.
// ==========================================================================

function initRelatedCarousel() {
  const track = document.querySelector('.related__grid');
  if (!track) return;

  const cards = Array.from(track.querySelectorAll('.related-card'));
  if (!cards.length) return;

  let active = null;
  let rafId  = null;

  function pick() {
    // Sub-pixel layouts mean scrollLeft rarely lands exactly on 0 or on the
    // maximum, hence the 2px tolerance on both ends.
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (track.scrollLeft <= 2)             return cards[0];
    if (track.scrollLeft >= maxScroll - 2) return cards[cards.length - 1];

    const centre = track.getBoundingClientRect().left + track.clientWidth / 2;
    let closest  = cards[0];
    let smallest = Infinity;
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      const dist = Math.abs(rect.left + rect.width / 2 - centre);
      if (dist < smallest) {
        smallest = dist;
        closest  = card;
      }
    }
    return closest;
  }

  function update() {
    rafId = null;
    const next = pick();
    if (next === active) return;
    active?.classList.remove('is-active');
    next.classList.add('is-active');
    active = next;
  }

  function schedule() {
    if (rafId === null) rafId = requestAnimationFrame(update);
  }

  track.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  update();
}

export function initProject() {
  initRelatedCarousel();

  const coverMobileLogo = document.querySelector('.cover-mobile-logo');
  const coverSection    = document.querySelector('.section--cover');
  const nav             = document.getElementById('nav');

  if (!coverSection) return;

  // Apply cover color class to nav (desktop link colors) and mobile topbar
  if (coverMobileLogo && nav) {
    const isDarkCover = !coverMobileLogo.classList.contains('cover-mobile-logo--dark');
    const coverClass  = isDarkCover ? 'nav--cover-light' : 'nav--cover-dark';
    nav.classList.add(coverClass);
    const mobileTopbar = document.getElementById('project-mobile-topbar');
    if (mobileTopbar) mobileTopbar.classList.add(coverClass);
  }

  if (!window.matchMedia('(max-width: 768px)').matches) {
    // Desktop only: show scroll arrow after 4s without scrolling, keep visible until past intro
    const scrollArrow = document.querySelector('.cover-scroll-arrow');
    if (scrollArrow) {
      let idleTimer = null;
      let shown = false;

      const show = () => { shown = true; gsap.to(scrollArrow, { opacity: 1, duration: 0.6, ease: 'power2.out' }); };
      const hide = () => { shown = false; gsap.to(scrollArrow, { opacity: 0, duration: 0.3, ease: 'power2.in' }); };

      const onScroll = () => {
        if (shown) return;
        clearTimeout(idleTimer);
        idleTimer = setTimeout(show, 4000);
      };

      ScrollTrigger.create({
        trigger: document.querySelector('.section--intro'),
        start: 'bottom top',
        onEnter: () => { clearTimeout(idleTimer); hide(); },
      });

      window.addEventListener('scroll', onScroll, { passive: true });
      idleTimer = setTimeout(show, 4000);
    }
  }

  if (!window.matchMedia('(max-width: 768px)').matches) return;

  // Mobile: pin the cover logo while cover section is in view
  if (coverMobileLogo) {
    ScrollTrigger.create({
      trigger:         coverSection,
      start:           'top top',
      end:             'bottom 50px',
      pin:             coverMobileLogo,
      pinnedContainer: coverSection,
      pinSpacing:      false,
      onInit(self) {
        if (self.pin.parentElement?.classList.contains('pin-spacer')) {
          self.pin.parentElement.style.zIndex = '0';
        }
      },
    });
  }

  // Mobile: update topbar styling when intro section enters view
  const mobileTopbar = document.getElementById('project-mobile-topbar');
  const introSection = document.querySelector('.section--intro');
  if (mobileTopbar && introSection) {
    const coverClass = mobileTopbar.classList.contains('nav--cover-light') ? 'nav--cover-light' : 'nav--cover-dark';
    ScrollTrigger.create({
      trigger:     introSection,
      start:       'top top',
      onEnter:     () => { mobileTopbar.classList.add('is-over-intro');    mobileTopbar.classList.remove('nav--cover-light', 'nav--cover-dark'); },
      onLeave:     () => { mobileTopbar.classList.remove('is-over-intro'); },
      onEnterBack: () => { mobileTopbar.classList.add('is-over-intro');    mobileTopbar.classList.remove('nav--cover-light', 'nav--cover-dark'); },
      onLeaveBack: () => { mobileTopbar.classList.remove('is-over-intro'); mobileTopbar.classList.add(coverClass); },
    });
  }
}
